/**
 * DOM input: keyboard (via the pure keymap), mouse picking, left / right drag camera rotation,
 * wheel zoom, mouse-button state for the control schemes (input/controls.ts) and autorun
 * (NumLock / Mouse4). Emits intents to handlers; it never builds Commands itself.
 *
 * Click vs drag (WoW): a release is a click when it comes within CLICK_MS of the press, or within
 * CLICK_SLOW_MS with less than CLICK_TRAVEL_RAD of camera travel. A drag can hide and lock the
 * cursor (pointer lock, WoW controls) until the last button is released.
 */
import type { UnitId } from '@keystone/sim';
import type { CameraRig } from '../render/camera';
import { type InputAction, type MoveKey, type UiBind, mapKey } from './bindings';
import type { MouseButtons } from './controls';

export interface InputHandlers {
  ability(bind: UiBind): void;
  tabTarget(reverse: boolean): void;
  partyTarget(slot: number): void;
  escape(): void;
  /** Shift (tap): dodge toward the held move direction. */
  dodge?(): void;
  /** +jump: Space. */
  jump?(): void;
  /** +glide: Space came up (folds the glider; input/glideHold.ts). Also on focus loss. */
  jumpUp?(): void;
  /** Left/right click on a unit (null = ground). */
  clickUnit(id: UnitId | null, button: 'left' | 'right'): void;
  /** Extra non-gameplay keys (pause, speed...) by KeyboardEvent.code. */
  other?(code: string): void;
  /**
   * The set of held move keys changed (keydown / keyup / focus loss). Called synchronously from
   * the event so movement can be applied at once instead of on the next frame poll.
   */
  moveChanged?(): void;
  /** Dev latency probe: a gameplay key went down (`t0` = KeyboardEvent.timeStamp). Called before the action. */
  probe?(kind: 'ability' | 'move' | 'tab', key: string, t0: number): void;
  /** A move key went down (not a repeat): W / S cancel autorun. Called before moveChanged. */
  moveKeyDown?(key: MoveKey): void;
  /** NumLock / Mouse4: toggle autorun. */
  autorun?(): void;
  /** Left / right mouse button state changed (mouselook, both-buttons run, autorun cancel). */
  buttonsChanged?(buttons: Readonly<MouseButtons>): void;
  /** Hide and lock the cursor while dragging (WoW controls). Default: never. */
  lockOnDrag?(): boolean;
  /** +castux: ground placement is up; its clicks confirm / cancel it, so they start no drag, mouselook or selection. */
  isPlacing?(): boolean;
  /** +castux: a mouse press on the game surface while placing (left confirms, right cancels). */
  placementClick?(button: 'left' | 'right'): void;
  /** +autoattack: the Attack key went down. */
  attack?(): void;
  /** +autoattack: KeyboardEvent.code of the Attack key (null = unbound). Checked before the keymap. */
  attackKey?(): string | null;
  /** +controls: KeyboardEvent.codes of the optional turn-left / turn-right keys (null = unbound); they win over the keymap. */
  turnKeys?(): { left: string | null; right: string | null };
}

export interface DomInput {
  readonly heldMoves: ReadonlySet<MoveKey>;
  /** Left / right mouse buttons held on the game surface (a press that started on the HUD doesn't count). */
  readonly buttons: Readonly<MouseButtons>;
  /** Last cursor position in client pixels (null when outside the canvas). */
  readonly cursor: { x: number; y: number } | null;
  enabled: boolean;
  dispose(): void;
}

/** A release this soon after the press is a click whatever the travel. */
export const CLICK_MS = 200;
/** A release this soon with less than CLICK_TRAVEL_RAD of camera travel is still a click. */
export const CLICK_SLOW_MS = 800;
/** ~2.25° of camera rotation (CameraRig.rotate turns 0.006 rad per pixel). */
export const CLICK_TRAVEL_RAD = (2.25 * Math.PI) / 180;
const RAD_PER_PX = 0.006;
/** +jump: a Shift press this short with no other key is a Dodge (longer holds are a modifier). */
export const SHIFT_TAP_MS = 350;
/** Mouse4 ("back" side button). */
const MOUSE4 = 3;

/** WoW's click-vs-drag rule for a button release. */
export function isClick(heldMs: number, travelRad: number): boolean {
  return heldMs < CLICK_MS || (heldMs < CLICK_SLOW_MS && travelRad < CLICK_TRAVEL_RAD);
}

export function attachDomInput(surface: HTMLElement, rig: CameraRig, pickUnit: (x: number, y: number) => UnitId | null, handlers: InputHandlers): DomInput {
  const held = new Set<MoveKey>();
  /** Turn keys held (code → key), so a release still matches after the setting changed. */
  const turnCodes = new Map<string, MoveKey>();
  let cursor: { x: number; y: number } | null = null;
  const buttons: MouseButtons = { left: false, right: false };
  /** Per held button (0 left, 2 right): press time and camera travel since. */
  const presses = new Map<number, { t: number; x: number; y: number; travel: number }>();
  let last: { x: number; y: number } | null = null;
  let locked = false;
  const state: DomInput = {
    heldMoves: held,
    buttons,
    get cursor() {
      return cursor;
    },
    enabled: true,
    dispose,
  };

  const isTyping = (e: KeyboardEvent): boolean => {
    const t = e.target as HTMLElement | null;
    return !!t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA');
  };

  function dispatch(a: InputAction): void {
    switch (a.kind) {
      case 'ability':
        return handlers.ability(a.bind);
      case 'tabTarget':
        return handlers.tabTarget(a.reverse);
      case 'partyTarget':
        return handlers.partyTarget(a.slot);
      case 'escape':
        return handlers.escape();
      case 'dodge':
        return handlers.dodge?.();
      case 'jump':
        return handlers.jump?.();
      case 'move':
      case 'autorun':
        return;
    }
  }

  /**
   * Shift is both Dodge and a modifier (Shift+Tab, Shift+F, shift-click): Dodge fires when Shift is
   * released within SHIFT_TAP_MS with nothing else pressed while it was down.
   */
  let shiftDown: { at: number; combo: boolean } | null = null;

  function onKeyDown(e: KeyboardEvent): void {
    if (!state.enabled || isTyping(e)) return;
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
      if (!e.repeat && !shiftDown) shiftDown = { at: e.timeStamp, combo: false };
      return;
    }
    if (shiftDown) shiftDown.combo = true;
    // +autoattack: a rebindable Attack key (Controls settings) wins over the default keymap.
    const attackCode = handlers.attackKey?.() ?? null;
    if (attackCode && e.code === attackCode && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      if (!e.repeat) handlers.attack?.();
      return;
    }
    // +controls: optional turn keys (Controls settings) win over the keymap (e.g. ← / Q).
    const tk = handlers.turnKeys?.();
    const turn: MoveKey | null = !tk || e.ctrlKey || e.metaKey || e.altKey ? null : e.code === tk.left ? 'turnLeft' : e.code === tk.right ? 'turnRight' : null;
    const a: InputAction | null = turn ? { kind: 'move', key: turn } : mapKey(e.code, e.shiftKey);
    if (turn) turnCodes.set(e.code, turn);
    if (!a) {
      handlers.other?.(e.code);
      return;
    }
    e.preventDefault();
    if (a.kind === 'move') {
      // OS key repeat re-sends keydown for a held key: only a new key changes anything.
      if (held.has(a.key)) return;
      handlers.probe?.('move', a.key, e.timeStamp);
      held.add(a.key);
      handlers.moveKeyDown?.(a.key);
      handlers.moveChanged?.();
      return;
    }
    if (a.kind === 'autorun') {
      if (!e.repeat) handlers.autorun?.();
      return;
    }
    if (e.repeat) return;
    if (a.kind === 'ability') handlers.probe?.('ability', a.bind, e.timeStamp);
    if (a.kind === 'tabTarget') handlers.probe?.('tab', 'Tab', e.timeStamp);
    dispatch(a);
  }

  function onKeyUp(e: KeyboardEvent): void {
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
      const s = shiftDown;
      shiftDown = null;
      if (s && !s.combo && e.timeStamp - s.at <= SHIFT_TAP_MS && state.enabled && !isTyping(e)) dispatch({ kind: 'dodge' });
      return;
    }
    const turn = turnCodes.get(e.code);
    if (turn) {
      turnCodes.delete(e.code);
      if (held.delete(turn)) handlers.moveChanged?.();
      return;
    }
    const a = mapKey(e.code);
    if (a?.kind === 'move' && held.delete(a.key)) handlers.moveChanged?.();
    else if (a?.kind === 'jump') handlers.jumpUp?.(); // +glide
  }

  const onBlur = (): void => {
    shiftDown = null;
    handlers.jumpUp?.(); // +glide: a held Space is gone with the focus
    releaseButtons();
    turnCodes.clear();
    if (held.size === 0) return;
    held.clear();
    handlers.moveChanged?.();
  };

  const syncButtons = (): void => {
    const left = presses.has(0);
    const right = presses.has(2);
    if (left === buttons.left && right === buttons.right) return;
    buttons.left = left;
    buttons.right = right;
    handlers.buttonsChanged?.(buttons);
  };

  const unlock = (): void => {
    if (!locked) return;
    locked = false;
    try {
      if (document.pointerLockElement === surface) document.exitPointerLock();
    } catch {
      /* not supported */
    }
  };

  const lock = (): void => {
    if (locked || !(handlers.lockOnDrag?.() ?? false)) return;
    locked = true;
    try {
      // Chrome returns a promise (rejects when the page may not lock now); others return nothing.
      const r = (surface as HTMLElement & { requestPointerLock(): Promise<void> | void }).requestPointerLock() as Promise<void> | undefined;
      if (r && typeof r.catch === 'function') r.catch(() => (locked = false));
    } catch {
      locked = false;
    }
  };

  function releaseButtons(): void {
    presses.clear();
    last = null;
    unlock();
    syncButtons();
  }

  function onMouseDown(e: MouseEvent): void {
    if (shiftDown) shiftDown.combo = true; // shift-click is a modifier, not a Dodge
    if (e.button === MOUSE4) {
      e.preventDefault();
      if (state.enabled) handlers.autorun?.();
      return;
    }
    if (e.button !== 0 && e.button !== 2) return;
    if (handlers.isPlacing?.()) {
      if (state.enabled) handlers.placementClick?.(e.button === 0 ? 'left' : 'right'); // +castux
      return;
    }
    presses.set(e.button, { t: e.timeStamp, x: e.clientX, y: e.clientY, travel: 0 });
    if (!locked) last = { x: e.clientX, y: e.clientY };
    syncButtons();
  }

  function onMouseMove(e: MouseEvent): void {
    if (!locked) cursor = { x: e.clientX, y: e.clientY };
    if (presses.size === 0) return;
    // Pointer-locked: the cursor stands still and only movementX/Y report the motion.
    let dx = e.movementX ?? 0;
    let dy = e.movementY ?? 0;
    if (!locked) {
      dx = last ? e.clientX - last.x : 0;
      dy = last ? e.clientY - last.y : 0;
      last = { x: e.clientX, y: e.clientY };
    }
    if (dx === 0 && dy === 0) return;
    // Left or right drag orbits the camera (WoW controls: right drag also steers the hero).
    rig.rotate(dx, dy);
    const travel = Math.hypot(dx, dy) * RAD_PER_PX;
    let dragging = false;
    for (const p of presses.values()) {
      p.travel += travel;
      if (p.travel >= CLICK_TRAVEL_RAD) dragging = true;
    }
    if (dragging) lock();
  }

  function onMouseUp(e: MouseEvent): void {
    if (e.button === MOUSE4) {
      e.preventDefault(); // no browser "back"
      return;
    }
    const p = presses.get(e.button);
    if (!p) return;
    presses.delete(e.button);
    if (presses.size === 0) {
      unlock();
      last = null;
    }
    syncButtons();
    if (!state.enabled || !isClick(e.timeStamp - p.t, p.travel)) return;
    // The press point (a locked cursor never moved; an unlocked one may have drifted a little).
    handlers.clickUnit(pickUnit(p.x, p.y), e.button === 0 ? 'left' : 'right');
  }

  const onLockChange = (): void => {
    // Esc (or the browser) dropped the lock mid-drag: the drag goes on unlocked.
    if (locked && document.pointerLockElement !== surface) {
      locked = false;
      last = null;
    }
  };

  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    rig.zoom(e.deltaY);
  }

  const onLeave = (): void => {
    cursor = null;
  };
  const onContext = (e: Event): void => e.preventDefault();

  // Capture phase: game keys are handled before anything else on the page sees them (no
  // focused element or bubbling handler can delay or swallow a press).
  window.addEventListener('keydown', onKeyDown, { capture: true });
  window.addEventListener('keyup', onKeyUp, { capture: true });
  window.addEventListener('blur', onBlur);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('mousemove', onMouseMove);
  document.addEventListener('pointerlockchange', onLockChange);
  surface.addEventListener('mousedown', onMouseDown);
  surface.addEventListener('mouseleave', onLeave);
  surface.addEventListener('wheel', onWheel, { passive: false });
  surface.addEventListener('contextmenu', onContext);

  function dispose(): void {
    window.removeEventListener('keydown', onKeyDown, { capture: true });
    window.removeEventListener('keyup', onKeyUp, { capture: true });
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('pointerlockchange', onLockChange);
    releaseButtons();
    surface.removeEventListener('mousedown', onMouseDown);
    surface.removeEventListener('mouseleave', onLeave);
    surface.removeEventListener('wheel', onWheel);
    surface.removeEventListener('contextmenu', onContext);
  }

  return state;
}
